import React, { useState, useEffect, useRef } from "react";
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
  CameraIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { USER_PROFILE, USER_ROLE } from '../../services/auth';
import placeholderImg from "../../assets/placeholder-user.png";
import { useBranches } from '../../context/branches';
import { downloadFile, downloadLocalFile } from '../../utils/fileDownload';
import { createDriver, resetPassword, updateDriverPartial } from '../../services/drivers';
import Spinner from '../../components/ui/Spinner';
import { toast } from 'react-toastify';

const DriverForm = ({ driver, driverType, onSave, onCancel }) => {

  const isEdit = Boolean(driver);
  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role == 'admin');
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
  const ownDriver = (driverType == 'own');

  const [photoPreview, setPhotoPreview] = useState(placeholderImg);
  // const { branches, loading: branch_loading, refresh } = useBranches({ reload: true, params: { status: 'active', paginate: false } });
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(driver || {});
  const [errors, setErrors] = useState([]);
  let branches = [], branch_loading = false, refreshBranches = null;
  if (showBranches) {
    const result = useBranches({ reload: false, params: { status: 'active', paginate: false } });
    branches = result.branches;
    branch_loading = result.loading;
    refreshBranches = result.refresh;
  }

  const [formData, setFormData] = useState({
    id: driver?.id || 0,
    name: driver?.name || "",
    driver_type: driver?.driver_type || driverType,
    primary_phone: driver?.primary_phone || "",
    secondary_phone: driver?.secondary_phone || "",
    email: driver?.email || "",
    photo_doc: driver?.photo_doc,
    password: "",
    branch: driver?.branch || "",
    branch_id: showBranches ? (driver?.branch?.id || 0) : profile.branch?.id,
    joining_date: driver?.joining_date || "",
    relieving_date: driver?.relieving_date || "",
    salary: driver?.salary || "",
    aadhar_number: driver?.aadhar_number || "",
    aadhar_doc: driver?.aadhar_doc || null,
    licence_number: driver?.licence_number || "",
    licence_expiry: driver?.licence_expiry || "",
    licence_doc: driver?.licence_doc || null,
    pan_number: driver?.pan_number || "",
    pan_doc: driver?.pan_doc || null,
    is_active: driver?.is_active || 1,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePassword, setChangePassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (formData.photo_doc instanceof File) {
      const url = URL.createObjectURL(formData.photo_doc);
      setPhotoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof formData.photo_doc === "string") {
      setPhotoPreview(formData.photo_doc);
    } else {
      setPhotoPreview(null);
    }
  }, [formData.photo_doc]);

  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (['primary_phone', 'secondary_phone'].includes(name)) {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (['name', 'email'].includes(name)) {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, [field]: file }));
    }
  };

  const openPhotoGallery = () => {
    const input = document.getElementById('photo-upload-gallery');
    if (input) input.click();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  const openPhotoCamera = () => {
    const tryOpenCamera = async () => {
      if (!navigator?.mediaDevices?.getUserMedia) {
        const input = document.getElementById('photo-upload-camera');
        if (input) input.click();
        return;
      }

      try {
        setCameraLoading(true);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        streamRef.current = stream;
        setCameraOpen(true);
      } catch (error) {
        const input = document.getElementById('photo-upload-camera');
        if (input) input.click();
      } finally {
        setCameraLoading(false);
      }
    };
    tryOpenCamera();
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const capturedFile = new File([blob], `driver-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFormData((prev) => ({ ...prev, photo_doc: capturedFile }));
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const handleDownload = (e, type) => {
    e.preventDefault();
    const file = formData[`${type}`];
    if (file instanceof File) {
      downloadLocalFile(file);
    } else if (typeof file === "string") {
      const filename = file.split('/').pop();
      downloadFile(file, filename);
    }
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!isEdit && ownDriver) {
      if (!formData.password) newErrors.password = 'Password is required';
    }
    if (!formData.primary_phone) newErrors.primary_phone = 'Primary Phone is required';
    if (!/^\d{10}$/.test(formData.primary_phone)) newErrors.primary_phone = 'Invalid primary phone number';
    if (formData.secondary_phone && !/^\d{10}$/.test(formData.secondary_phone)) newErrors.secondary_phone = 'Invalid secondary phone number';
    // if (!formData.email) newErrors.email = 'Email is required';
    if (formData.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) newErrors.email = 'Invalid email';
    if (role == 'admin') {
      if (!formData.branch || formData.branch <= 0) newErrors.branch = 'Select atleast one branch.';
    }
    if (formData.aadhar_number && !/^\d{12}$/.test(formData.aadhar_number)) newErrors.aadhar_number = 'Invalid aadhar number';
    // if (formData.licence_number && !/^[A-Z]{2}\d{2}[A-Z]{2}\d{4,7}$/.test(formData.licence_number)) newErrors.licence_number = 'Invalid licence number. Example: TN22AF5634123';
    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_number)) newErrors.pan_number = 'Invalid PAN number';
    return newErrors;
  };

  const cleanupData = () => {
    const driver = { ...formData };
    driver.is_active = driver.is_active ? 1 : 0;
    if (driver.id == 0 &&  'driver_code' in driver) {
      delete driver.driver_code;
    }
    if (!driver.aadhar_doc) {
      delete driver.aadhar_doc;
    }
    if (!driver.licence_doc) {
      delete driver.licence_doc;
    }
    if (!driver.pan_doc) {
      delete driver.pan_doc;
    }
    return driver;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    const errors = validate();
    const driver = cleanupData();
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    let err, response;
    setLoading(true);
    if (driver.id) {
      const changedFields = getChangedFields(initialData, driver);
      if (Object.keys(changedFields).length === 0) {
        return;
      }
      [err, response] = await updateDriverPartial(driver.id, changedFields);
    } else {
      driver.is_active = 1;
      [err, response] = await createDriver(driver);
    }
    setLoading(false);
    if (err) {
      setErrors(err);
    } else {
      onSave(response);
    }
  };

  const updateBranch = (index) => {
    setFormData({ ...formData, branch: branches[index], branch_id: branches[index].id });
  }

  const renderDocField = (label, field) => (
    <div>
      <div className="mt-1 flex items-center gap-2">
        <label className="cursor-pointer bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 uppercase">
          Choose File
          <input
            type="file"
            onChange={(e) => handleFileChange(e, field)}
            className="hidden"
          />
        </label>
        {formData[field] && (
          <button
              onClick={(e) => handleDownload(e, field)}
              className="text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-50"
              title="Download"
            >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  const handleChangePassword = () => {
    setChangePassword("");
    setConfirmPassword("");
    setChangePasswordError("");
    setShowResetPassword(false);
    setShowChangePasswordModal(true);
  };

  const changePasswordInServer = async (onSuccess, onFailed) => {
    let params = { password: changePassword };
    await resetPassword(formData.id, params).then(([err, data]) => {
      onSuccess(err, data);
    })
    .catch((error) => {
      onFailed(error);
    });
  };

  const handlePasswordResetSubmit = () => {
    if (!changePassword || !confirmPassword) {
      setChangePasswordError("Both fields are required");
      return;
    }

    // if (changePassword.length < 8) {
    //   setChangePasswordError("Password must be at least 8 characters");
    //   return;
    // }

    if (changePassword !== confirmPassword) {
      setChangePasswordError("Passwords do not match");
      return;
    }

    // update only password
    changePasswordInServer((err, data) => {
      if (!err) {
        setShowChangePasswordModal(false);
        toast.success("Password Saved Successfully.");
      }
    }, (error) => {
    });

  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 p-4 bg-white rounded shadow [&_input[type='text']]:uppercase [&_textarea]:uppercase [&_select]:uppercase"
    >
      {/* BASIC DETAILS */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <h2 className="text-lg font-semibold mb-4 sm:mb-0 uppercase">Basic Details</h2>
          {ownDriver && (
            <div className="relative w-32 h-32 flex-shrink-0 border rounded-lg overflow-hidden bg-gray-50 group">
              <img
                src={photoPreview || placeholderImg}
                alt="Driver"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={openPhotoGallery}
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition"
              >
                {photoPreview ? (
                  <PencilIcon className="w-6 h-6 text-white bg-black bg-opacity-70 p-1 rounded-full" />
                ) : (
                  <CameraIcon className="w-6 h-6 text-white bg-black bg-opacity-70 p-1 rounded-full" />
                )}
              </button>
              <button
                type="button"
                onClick={openPhotoCamera}
                className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded-md hover:bg-black/80 disabled:opacity-60"
                disabled={cameraLoading}
              >
                {cameraLoading ? 'Opening...' : 'Camera'}
              </button>
              <input
                id="photo-upload-gallery"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e, "photo_doc")}
              />
              <input
                id="photo-upload-camera"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileChange(e, "photo_doc")}
              />
            </div>
          )}
        </div>
        {cameraOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-4">
              <h3 className="text-base font-semibold mb-3">Capture Driver Photo</h3>
              <div className="rounded-md overflow-hidden bg-black">
                <video ref={videoRef} className="w-full h-auto" playsInline autoPlay muted />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={stopCamera}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Capture
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              placeholder="Name"
              className="border p-2 rounded w-full"
            />
            {validationErrors.name && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
            )}
          </div>
          { ownDriver && (
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  type={showFormPassword ? "text" : "password"}
                  name="password"
                  value={formData.password || ""}
                  onChange={handleInputChange}
                  placeholder="Password"
                  required={!isEdit}
                  className="border p-2 rounded w-full pr-10 normal-case [text-transform:none]"
                />
                <button
                  type="button"
                  onClick={() => setShowFormPassword(!showFormPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showFormPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
              )}
            </div>
          )}
          {role === "admin" && (
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Branch</label>
              <select
                name="branch"
                value={formData?.branch?.id}
                onChange={(e) => updateBranch(e.target.selectedIndex - 1)}
                className="
                  border p-2 rounded w-full
                  disabled:bg-gray-100 disabled:text-gray-500
                  disabled:cursor-not-allowed
                "
                disabled={formData?.id > 0}
              >
                <option value="">Select Branch</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              {validationErrors.branch && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.branch}</p>
              )}
            </div>
          )}
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700">Primary Phone</label>
            <input
              type="text"
              name="primary_phone"
              value={formData.primary_phone || ""}
              onChange={handleInputChange}
              placeholder="Primary Contact"
              className="border p-2 rounded w-full"
            />
            {validationErrors.primary_phone && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.primary_phone}</p>
            )}
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700">Secondary Phone</label>
            <input
              type="text"
              name="secondary_phone"
              value={formData.secondary_phone || ""}
              onChange={handleInputChange}
              placeholder="Secondary Contact"
              className="border p-2 rounded w-full"
            />
            {validationErrors.secondary_phone && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.secondary_phone}</p>
            )}
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              placeholder="Email"
              className="border p-2 rounded w-full"
            />
            {validationErrors.email && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
            )}
          </div>
        </div>
      </div>

      {/* EMPLOYMENT DETAILS */}
      { ownDriver && (
        <div className="bg-white p-4 rounded-xl shadow-md">
          <h2 className="text-lg font-semibold mb-4 uppercase">Employment Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Joining Date</label>
              <input
                type="date"
                name="joining_date"
                value={formData.joining_date || ""}
                onChange={handleInputChange}
                className="border p-2 rounded w-full"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Relieving Date</label>
              <input
                type="date"
                name="relieving_date"
                value={formData.relieving_date || ""}
                onChange={handleInputChange}
                className="border p-2 rounded w-full"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Salary</label>
              <input
                type="number"
                name="salary"
                value={formData.salary || ""}
                onChange={handleInputChange}
                placeholder="Salary"
                className="border p-2 rounded w-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENTS */}
      { ownDriver && (
        <div className="bg-white p-4 rounded-xl shadow-md">
          <h2 className="text-lg font-semibold mb-4 uppercase">Documents</h2>

          {/* Aadhar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Aadhar Number</label>
              <input
                type="text"
                name="aadhar_number"
                value={formData.aadhar_number || ""}
                onChange={handleInputChange}
                placeholder="Aadhar Number"
                className="border p-2 rounded w-full"
              />
              {validationErrors.aadhar_number && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.aadhar_number}</p>
              )}
              {renderDocField("Aadhar Document", "aadhar_doc")}
            </div>
          </div>

          {/* Licence */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Licence Number</label>
              <input
                type="text"
                name="licence_number"
                value={formData.licence_number || ""}
                onChange={handleInputChange}
                placeholder="Licence Number"
                className="border p-2 rounded w-full"
              />
              {validationErrors.licence_number && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.licence_number}</p>
              )}
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Licence Expiry</label>
              <input
                type="date"
                name="licence_expiry"
                value={formData.licence_expiry || ""}
                onChange={handleInputChange}
                className="border p-2 rounded w-full"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Licence Document</label>
              {renderDocField("Licence Document", "licence_doc")}
            </div>
          </div>

          {/* PAN */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700">PAN</label>
              <input
                type="text"
                name="pan_number"
                value={formData.pan_number || ""}
                onChange={handleInputChange}
                placeholder="PAN Number"
                className="border p-2 rounded w-full"
              />
              {validationErrors.pan_number && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.pan_number}</p>
              )}
              {renderDocField("PAN Document", "pan_doc")}
            </div>
          </div>
        </div>
      )}

      {/* ACTION BUTTONS */}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 rounded"
        >
          Cancel
        </button>
        {ownDriver && isEdit && (
          <button
            type="button"
            onClick={handleChangePassword}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Change Password
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 flex items-center justify-center bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 relative min-w-[80px"
          disabled={loading}
        >
          <span className={`${loading ? "opacity-0" : "opacity-100"} transition-opacity`}>
            Save
          </span>
          {loading && (
            <span className="absolute inset-0 flex items-center justify-center z-10">
              <Spinner size={18} color="#fff" inline />
            </span>
          )}
        </button>
      </div>
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Reset Password</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">New Password</label>
                <input
                  type={showResetPassword ? "text" : "password"}
                  value={changePassword}
                  onChange={(e) => setChangePassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 normal-case [text-transform:none]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <input
                  type={showResetPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 normal-case [text-transform:none]"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowResetPassword(!showResetPassword)}
                className="text-sm text-blue-600 hover:underline"
              >
                {showResetPassword ? "Hide Password" : "Show Password"}
              </button>

              {changePasswordError && (
                <p className="text-red-500 text-sm">{changePasswordError}</p>
              )}
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePasswordResetSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default DriverForm;
